import { formatQuantity } from '@kronos/domain'
import type { InventoryItem } from '../../data/inventory'
import { Link } from '../shell/Link'
import { ItemImage } from './ItemArt'
import { ShelfFigure, StockBadges, StockMeter } from './Stock'

/**
 * The gallery: one tile per item, the picture first.
 *
 * The way Sortly and Odoo's kanban show stock — a photo you recognise before
 * you read anything, the name under it, and the number that answers "do we
 * have any?" large enough to read from across a bench. The whole tile is the
 * button to the item page; it lifts on hover so it reads as one.
 */
export function ItemGrid({ items }: { items: readonly InventoryItem[] }): JSX.Element {
  return (
    <div className="inv-grid">
      {items.map((item) => (
        <ItemTile key={item.id} item={item} />
      ))}
    </div>
  )
}

function ItemTile({ item }: { item: InventoryItem }): JSX.Element {
  const serialized = item.tracking === 'serialized'
  return (
    <Link to={{ name: 'item', id: item.id }} className="inv-tile">
      <span className="inv-tile__media">
        <ItemImage imageId={item.imageId} category={item.category} name={item.name} />
        <StockBadges item={item} max={2} />
      </span>
      <span className="inv-tile__body">
        <span className="inv-tile__name">{item.name}</span>
        <span className="inv-tile__meta">{[item.category, item.partNumber].filter(Boolean).join(' · ') || 'No category'}</span>
        <span className="inv-tile__stock">
          <ShelfFigure item={item} />
          {item.fitted > 0 ? (
            <span className="inv-tile__fitted">
              {formatQuantity(item.fitted, serialized ? 'each' : item.unit)} {serialized ? 'fitted' : 'used'}
            </span>
          ) : null}
        </span>
        <StockMeter item={item} />
      </span>
    </Link>
  )
}
